import React, { ElementType, PropsWithChildren, ReactNode, createContext, useContext, useState, useCallback, useMemo } from "react";
import MuiMenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import { Icon } from "@/components/Icon";
import { MenuProps, PopoverOrigin } from "@mui/material";
import { css, styled } from "@mui/material/styles";

export type ContextMenuOptions = {
  onMenuOpen?: (event: React.MouseEvent) => void;
  onMenuClose?: () => void;
  /** Menu position offset from mouse */
  offset?: [number, number];
};

export const useContextMenu = (options: ContextMenuOptions) => {
  const { onMenuOpen, onMenuClose, offset } = options;
  const [offsetX, offsetY] = offset ?? [0, 0];
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const onContextMenuInternal = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - offsetX, mouseY: event.clientY - offsetY }
        : null,
    );
    onMenuOpen?.(event);
  }, [contextMenu, offsetX, offsetY, onMenuOpen, setContextMenu]);

  const closeInternal = useCallback(() => {
    setContextMenu(null);
    onMenuClose?.();
  }, [onMenuClose, setContextMenu]);

  const anchorPosition = useMemo(() => (contextMenu !== null
    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
    : undefined), [contextMenu]);

  const menuHookProps = useMemo((): MenuProps => ({
    open: contextMenu !== null,
    onClose: closeInternal,
    anchorReference: "anchorPosition",
    anchorPosition: anchorPosition,
    slotProps: {
      root: {
        onContextMenu: (e: React.MouseEvent) => {
          e.preventDefault();
          closeInternal();
        },
      },
    }
  }), [contextMenu, closeInternal, anchorPosition]);

  return useMemo(() => ({
    onContextMenu: onContextMenuInternal,
    close: closeInternal,
    isOpen: contextMenu !== null,
    anchorPosition,
    contextMenu,
    props: menuHookProps
  }), [onContextMenuInternal, closeInternal, contextMenu, anchorPosition, menuHookProps]);
};

export const createContextMenuContext = (name: string) => {
  const _context = createContext<ReturnType<typeof useContextMenu>>(null as unknown as ReturnType<typeof useContextMenu>);
  return {
    context: _context,
    Consumer: _context.Consumer,
    useContext: () => {
      const context = useContext(_context);
      if (context === null) {
        throw new Error(`[${name}:useContext] useContext must be used within the context provider`);
      }
      return context;
    },
    Provider: ({ children, options }: { options: ContextMenuOptions } & PropsWithChildren) => {
      const contextMenu = useContextMenu(options);
      return (
        <_context.Provider value={contextMenu}>
          {children}
        </_context.Provider>
      );
    },
  };
};

export const useContextMenuValue = <T,>(selector: (event: React.MouseEvent) => T) => {
  const [value, setValue] = useState<T>();

  const contextMenuOptions = React.useMemo(() => ({
    onMenuOpen: (event: React.MouseEvent) => {
      const val = selector(event);
      setValue(val);
    },
  }), [selector, setValue]);

  return { value, options: contextMenuOptions };
};

export type usePopverOptions = {
  matchButtonWidth?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

export const usePopover = (id: string, options: usePopverOptions = {}) => {
  const { matchButtonWidth = false } = options;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const _id = open ? id : undefined;
  const width = matchButtonWidth ? anchorEl?.offsetWidth : undefined;
  const PaperProps = { sx: { mt: 0.5, width } };

  const anchorOrigin =
    options.anchorOrigin ||
    ({ vertical: "bottom", horizontal: "center" } as PopoverOrigin);

  const transformOrigin =
    options.transformOrigin ||
    ({ vertical: "top", horizontal: "center" } as PopoverOrigin);

  return { anchorEl, onClick: handleClick, onClose: handleClose, open, id: _id, anchorOrigin, transformOrigin, PaperProps };
};

export const MenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== "error" && prop !== "hide",
  name: "MenuItem",
  slot: "Root",
})<{ error?: boolean, hide?: boolean }>(
  ({ error, hide, theme }) => hide ? { display: "none" } : error ? css`
    color: ${theme.palette.error.main};
    &:hover {
      color: ${theme.palette.common.white};
      background-color: ${theme.palette.error.main};
    }
  ` : {});

export const IconMenuItem = <C extends ElementType>({ icon, ...props }: MenuItemProps<C, { icon: string | ReactNode }>) => (
  <MenuItem sx={{ gap: 1 }} {...props}>
    {icon && (typeof icon === "string" ? <Icon icon={icon} /> : icon)}
    {props.children}
  </MenuItem>
);
